import { FreightGetHelpReasonCode } from './freight-get-help-reason-codes';

export type GetHelpFieldValues = {
  proNumber: string | null;
  bolNumber: string;
  carrierScac: string | null;
  customerNumber: string | null;
  sellerId: string | null;
  phone: string | null;
  shipDate: string;
  shipmentDeliveryDate: string | null;
  originCountryCode: string | null;
  destinationCountryCode: string | null;
  isShipmentInsured: boolean | null;
  insuredValue: number | null;
};

export const FREIGHT_GET_HELP_FORM_URL = 'https://help.shipstation.com/hc/en-us/p/rai';

// Case Type and Line of Business have no Salesforce field, so they're keyed by numeric Zendesk field ID instead.
export const CASE_TYPE_FIELD_ID = '54608575136539';
export const LINE_OF_BUSINESS_FIELD_ID = '54424659531035';

export const RAI_SALESFORCE_FIELD_NAMES = {
  proOrTracking: 'PRO__c',
  bolNumber: 'BOL_Number__c',
  scac: 'SCAC__c',
  phone: 'SuppliedPhone',
  auctaneMerchantId: 'Auctane_Merchant_ID__c',
  account: 'Account__c',
  reasonCode: 'Reason_Code__c',
  shipmentDate: 'Shipment_Date__c',
  shipmentDeliveryDate: 'Shipment_Delivery_Date__c',
  domesticInternational: 'Domestic_International__c',
  shipmentInsured: 'Shipment_Insured__c',
  totalValueInsured: 'Total_Value_Insured__c',
} as const;

const isBlank = (value: string | null): value is null | '' => value === null || value.trim() === '';

// A literal "+" must go through URLSearchParams (as %2B) rather than manual concatenation, or it's
// indistinguishable from an encoded space.
const buildTextFieldParams = (textFieldsByName: [string, string | null][]): Record<string, string> =>
  textFieldsByName.reduce<Record<string, string>>(
    (acc, [fieldName, value]) => (isBlank(value) ? acc : { ...acc, [`tf_${fieldName}`]: value }),
    {},
  );

// Always LTL for now, per the field-mapping sheet.
const LINE_OF_BUSINESS_LTL_TAG = 'tf_wwex_lineofbusiness_ltl';

// Also used by Sierra chat.
export const RAI_CASE_TYPE_TAG_BY_REASON_CODE: Record<FreightGetHelpReasonCode, string> = {
  '001': 'tf_wwex_rai_lost_shipment',
  '002': 'tf_wwex_rai_damaged_shipment',
  '003': 'tf_wwex_rai_billing_error',
  '004': 'tf_wwex_rai_incorrect_dim_weight',
  '005': 'tf_wwex_rai_invalid_accessorial_fees',
  '009': 'tf_wwex_rai_late_delivery',
};

// SuppliedName/SuppliedEmail are intentionally not sent — Zendesk prefills them from the
// SSO-authenticated ticket requester. Seller ID itself is never sent raw — only the
// "STN-"-prefixed Auctane Merchant ID.
const buildUniversalFieldParams = (reasonCode: FreightGetHelpReasonCode, context: GetHelpFieldValues): Record<string, string> => {
  const auctaneMerchantId = context.sellerId ? `STN-${context.sellerId}` : null;

  const params = buildTextFieldParams([
    [RAI_SALESFORCE_FIELD_NAMES.reasonCode, reasonCode],
    [RAI_SALESFORCE_FIELD_NAMES.proOrTracking, context.proNumber],
    [RAI_SALESFORCE_FIELD_NAMES.bolNumber, context.bolNumber],
    [RAI_SALESFORCE_FIELD_NAMES.scac, context.carrierScac],
    [RAI_SALESFORCE_FIELD_NAMES.phone, context.phone],
    [RAI_SALESFORCE_FIELD_NAMES.auctaneMerchantId, auctaneMerchantId],
    // Distinct from the STN-prefixed Auctane Merchant ID above.
    [RAI_SALESFORCE_FIELD_NAMES.account, context.customerNumber],
  ]);

  params[`tf_${LINE_OF_BUSINESS_FIELD_ID}`] = LINE_OF_BUSINESS_LTL_TAG;
  params[`tf_${CASE_TYPE_FIELD_ID}`] = RAI_CASE_TYPE_TAG_BY_REASON_CODE[reasonCode];

  return params;
};

// Per the field-mapping sheet's tabs: documented everywhere except Late Delivery.
const REASON_CODES_WITH_SHIPMENT_DATE: readonly FreightGetHelpReasonCode[] = [
  FreightGetHelpReasonCode.LostShipment,
  FreightGetHelpReasonCode.BillingErrorShipment,
  FreightGetHelpReasonCode.IncorrectDimensionsOrWeight,
  FreightGetHelpReasonCode.InvalidAccessorialFee,
  FreightGetHelpReasonCode.DamagedShipment,
];

// Documented only on these three tabs.
const REASON_CODES_WITH_SHIPMENT_DELIVERY_DATE: readonly FreightGetHelpReasonCode[] = [
  FreightGetHelpReasonCode.BillingErrorShipment,
  FreightGetHelpReasonCode.IncorrectDimensionsOrWeight,
  FreightGetHelpReasonCode.InvalidAccessorialFee,
];

// Zendesk date fields expect a bare `YYYY-MM-DD`; both sources carry a full UTC timestamp.
const toZendeskDate = (isoDateTime: string): string => isoDateTime.split('T')[0];

const buildShipmentDateFieldParams = (reasonCode: FreightGetHelpReasonCode, context: GetHelpFieldValues): Record<string, string> => {
  const shipmentDate =
    REASON_CODES_WITH_SHIPMENT_DATE.includes(reasonCode) && !isBlank(context.shipDate) ? toZendeskDate(context.shipDate) : null;

  const shipmentDeliveryDate =
    REASON_CODES_WITH_SHIPMENT_DELIVERY_DATE.includes(reasonCode) && !isBlank(context.shipmentDeliveryDate)
      ? toZendeskDate(context.shipmentDeliveryDate)
      : null;

  return buildTextFieldParams([
    [RAI_SALESFORCE_FIELD_NAMES.shipmentDate, shipmentDate],
    [RAI_SALESFORCE_FIELD_NAMES.shipmentDeliveryDate, shipmentDeliveryDate],
  ]);
};

const REASON_CODES_WITH_DOMESTIC_INTERNATIONAL: readonly FreightGetHelpReasonCode[] = [
  FreightGetHelpReasonCode.LateDelivery,
  FreightGetHelpReasonCode.BillingErrorShipment,
];

const DOMESTIC_INTERNATIONAL_TAG = {
  domestic: 'tf_wwex_domestic',
  internationalExport: 'tf_wwex_international_export',
  internationalImport: 'tf_wwex_international_import',
} as const;

// WWEX LTL shipments are anchored in the US.
const HOME_COUNTRY_CODE = 'US';

const resolveDomesticInternationalTag = (originCountryCode: string | null, destinationCountryCode: string | null): string | null => {
  if (!originCountryCode || !destinationCountryCode) return null;
  if (originCountryCode === destinationCountryCode) return DOMESTIC_INTERNATIONAL_TAG.domestic;
  if (originCountryCode === HOME_COUNTRY_CODE) return DOMESTIC_INTERNATIONAL_TAG.internationalExport;
  if (destinationCountryCode === HOME_COUNTRY_CODE) return DOMESTIC_INTERNATIONAL_TAG.internationalImport;
  return null;
};

const buildDomesticInternationalParams = (reasonCode: FreightGetHelpReasonCode, context: GetHelpFieldValues): Record<string, string> => {
  if (!REASON_CODES_WITH_DOMESTIC_INTERNATIONAL.includes(reasonCode)) return {};

  const tag = resolveDomesticInternationalTag(context.originCountryCode, context.destinationCountryCode);
  return tag ? { [`tf_${RAI_SALESFORCE_FIELD_NAMES.domesticInternational}`]: tag } : {};
};

// Only Lost Shipment and Damaged Shipment document these fields.
const REASON_CODES_WITH_SHIPMENT_INSURANCE: readonly FreightGetHelpReasonCode[] = [
  FreightGetHelpReasonCode.LostShipment,
  FreightGetHelpReasonCode.DamagedShipment,
];

// A plain text field on the Zendesk side despite being a Salesforce picklist — expects "Yes"/"No", not a tag.
const SHIPMENT_INSURED_TEXT = { yes: 'Yes', no: 'No' } as const;

const buildShipmentInsuranceParams = (reasonCode: FreightGetHelpReasonCode, context: GetHelpFieldValues): Record<string, string> => {
  if (!REASON_CODES_WITH_SHIPMENT_INSURANCE.includes(reasonCode)) return {};
  if (context.isShipmentInsured === null) return {};

  const params: Record<string, string> = {
    [`tf_${RAI_SALESFORCE_FIELD_NAMES.shipmentInsured}`]: context.isShipmentInsured ? SHIPMENT_INSURED_TEXT.yes : SHIPMENT_INSURED_TEXT.no,
  };

  if (context.isShipmentInsured && context.insuredValue !== null) {
    params[`tf_${RAI_SALESFORCE_FIELD_NAMES.totalValueInsured}`] = String(context.insuredValue);
  }

  return params;
};

export const buildFreightGetHelpFormUrl = (reasonCode: FreightGetHelpReasonCode, context: GetHelpFieldValues): string => {
  const params = new URLSearchParams({
    ...buildUniversalFieldParams(reasonCode, context),
    ...buildShipmentDateFieldParams(reasonCode, context),
    ...buildDomesticInternationalParams(reasonCode, context),
    ...buildShipmentInsuranceParams(reasonCode, context),
  });

  return `${FREIGHT_GET_HELP_FORM_URL}?${params.toString()}`;
};
